import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useTranslation } from "react-i18next";
import SidebarSection from "../../components/TeamChat/SidebarSection";
import PortalTeamInboxItem from "./PortalTeamInboxItem";
import PortalTeamUserRow from "./PortalTeamUserRow";
import { findUserDmSpace } from "../Team/utils/teamUiUtils";
import { sumSpaceUnreadCount } from "../Team/utils/teamSpaceGroups";
import {
  searchPartnerPortalTeamContacts,
  searchPlatformPortalTeamContacts,
} from "../../api/client";

const EXPANDED_STORAGE_KEY = "xproPortalTeamExpandedWorkspaces";
const CONTACT_SEARCH_MIN = 2;
const CONTACT_SEARCH_DEBOUNCE_MS = 350;

function loadExpandedSet() {
  try {
    const raw = localStorage.getItem(EXPANDED_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.map(String));
  } catch {
    return new Set();
  }
}

function saveExpandedSet(set) {
  try {
    localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

const PortalTeamSidebar = ({
  containerStyle,
  isMobile,
  mode = "platform",
  directory,
  loading,
  activeWorkspace,
  activeSpaceId,
  onSelectConversation,
  onOpenUser,
  openingUserKey,
  onLoadWorkspace,
  isWorkspaceLoading,
  isWorkspaceLoaded,
}) => {
  const { t } = useTranslation("team");
  const { t: tApp } = useTranslation("app");
  const { t: tLive } = useTranslation("settings");
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(loadExpandedSet);
  const [sectionCollapsed, setSectionCollapsed] = useState({});
  const [contactResults, setContactResults] = useState([]);
  const [contactSearching, setContactSearching] = useState(false);

  const workspaces = useMemo(() => (Array.isArray(directory) ? directory : []), [directory]);
  const searchContacts =
    mode === "partner" ? searchPartnerPortalTeamContacts : searchPlatformPortalTeamContacts;

  useEffect(() => {
    if (!activeWorkspace) return;
    setExpanded((prev) => {
      if (prev.has(activeWorkspace)) return prev;
      const next = new Set(prev);
      next.add(activeWorkspace);
      saveExpandedSet(next);
      return next;
    });
  }, [activeWorkspace]);

  useEffect(() => {
    for (const slug of expanded) {
      onLoadWorkspace?.(slug);
    }
  }, [expanded, onLoadWorkspace]);

  useEffect(() => {
    const q = search.trim();
    if (q.length < CONTACT_SEARCH_MIN) {
      setContactResults([]);
      setContactSearching(false);
      return undefined;
    }

    setContactSearching(true);
    const timer = window.setTimeout(() => {
      searchContacts(q)
        .then((rows) => setContactResults(Array.isArray(rows) ? rows : []))
        .catch(() => setContactResults([]))
        .finally(() => setContactSearching(false));
    }, CONTACT_SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [search, searchContacts]);

  const handleAccordionChange = useCallback(
    (slug) => (_event, isExpanded) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (isExpanded) next.add(slug);
        else next.delete(slug);
        saveExpandedSet(next);
        return next;
      });
      if (isExpanded) onLoadWorkspace?.(slug);
    },
    [onLoadWorkspace]
  );

  const toggleSection = useCallback((workspaceSlug, key) => {
    const sectionKey = `${workspaceSlug}:${key}`;
    setSectionCollapsed((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  }, []);

  const isSectionCollapsed = useCallback(
    (workspaceSlug, key) => Boolean(sectionCollapsed[`${workspaceSlug}:${key}`]),
    [sectionCollapsed]
  );

  const filteredWorkspaces = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || q.length >= CONTACT_SEARCH_MIN) return workspaces;

    return workspaces
      .map((ws) => {
        const wsLabel = String(ws.workspaceLabel || ws.workspaceSlug || "").toLowerCase();
        const conversations = (ws.conversations || []).filter((c) => {
          const label = String(c.dmPeerName || c.name || "").toLowerCase();
          const preview = String(c.lastMessagePreview || "").toLowerCase();
          return label.includes(q) || preview.includes(q) || wsLabel.includes(q);
        });
        const activeUsers = (ws.activeUsers || []).filter((u) => {
          const name = String(u.userName || "").toLowerCase();
          return name.includes(q) || wsLabel.includes(q);
        });
        if (!conversations.length && !activeUsers.length && !wsLabel.includes(q)) return null;
        return { ...ws, conversations, activeUsers };
      })
      .filter(Boolean);
  }, [workspaces, search]);

  const showContactResults = search.trim().length >= CONTACT_SEARCH_MIN;

  const handleContactSelect = useCallback(
    (contact) => {
      if (!contact?.workspaceSlug || !contact?.userId) return;
      const ws = workspaces.find((w) => w.workspaceSlug === contact.workspaceSlug) || {
        workspaceSlug: contact.workspaceSlug,
        workspaceLabel: contact.workspaceLabel || contact.workspaceSlug,
        conversations: [],
      };
      const existing = findUserDmSpace(ws.conversations, contact.userId);
      onOpenUser(
        ws,
        {
          userId: contact.userId,
          userName: contact.userName,
          userAvatarUrl: contact.userAvatarUrl,
          presence: "away",
        },
        existing
      );
    },
    [workspaces, onOpenUser]
  );

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : { xs: "100%", md: "25%" },
        minWidth: isMobile ? 0 : 260,
        maxWidth: isMobile ? "100%" : 360,
        flex: isMobile ? 1 : undefined,
        bgcolor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        display: "flex",
        flexDirection: "column",
        alignSelf: "stretch",
        flexShrink: 0,
        minHeight: 0,
        overflow: "hidden",
        ...containerStyle,
      }}
    >
      <Box sx={{ px: 2, pt: 1.75, pb: 1.25, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Typography variant="subtitle1" fontWeight={700} color="text.primary">
          {tApp("platformPage.portalTeamTitle", { defaultValue: t("title") })}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25 }}>
          {tApp("platformPage.portalTeamSubtitle", { defaultValue: t("sidebarSubtitle") })}
        </Typography>

        <TextField
          size="small"
          fullWidth
          placeholder={tApp("platformPage.portalTeamSearchPlaceholder", {
            defaultValue: "Buscar usuário ou conversa…",
          })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: contactSearching ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null,
          }}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "action.hover" },
            "& .MuiOutlinedInput-input::placeholder": {
              color: theme.palette.input?.placeholder || theme.palette.text.secondary,
              opacity: 1,
            },
          }}
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", py: 0.5 }} className="sidebar-scroll">
        {loading ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : showContactResults ? (
          <Box sx={{ px: 1, py: 0.5 }}>
            <Typography variant="overline" sx={{ px: 1, color: "text.secondary" }}>
              {tApp("platformPage.portalTeamSearchResults", { defaultValue: "Usuários" })}
            </Typography>
            {!contactSearching && !contactResults.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 2 }}>
                {t("noSearchResults")}
              </Typography>
            ) : (
              <List dense disablePadding>
                {contactResults.map((contact) => {
                  const key = `${contact.workspaceSlug}:${contact.userId}`;
                  return (
                    <ListItemButton
                      key={key}
                      onClick={() => handleContactSelect(contact)}
                      sx={{ borderRadius: 1.5, mx: 0.5, my: 0.25 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <PersonOutlineIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={contact.userName || contact.userEmail}
                        secondary={contact.workspaceLabel || contact.workspaceSlug}
                        primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                        secondaryTypographyProps={{ noWrap: true }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>
        ) : !filteredWorkspaces.length ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <ForumOutlinedIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {search.trim() ? t("noSearchResults") : t("noDms")}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0.5} sx={{ px: 0.5 }}>
            {filteredWorkspaces.map((ws) => {
              const slug = ws.workspaceSlug;
              const isOpen = expanded.has(slug);
              const wsLoading = isWorkspaceLoading?.(slug);
              const wsLoaded = isWorkspaceLoaded?.(slug);
              const dmCount = ws.conversations?.length || 0;
              const dmUnread = sumSpaceUnreadCount(ws.conversations);
              const activeUsers = ws.activeUsers || [];
              const activeCount = activeUsers.length;
              const onlineCount = activeUsers.filter((u) => u.presence === "online").length;

              return (
                <Accordion
                  key={slug}
                  expanded={isOpen}
                  onChange={handleAccordionChange(slug)}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1.5,
                    "&:before": { display: "none" },
                    bgcolor: "background.paper",
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      px: 1.25,
                      minHeight: 48,
                      "& .MuiAccordionSummary-content": { my: 0.75, alignItems: "center", gap: 0.75 },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.primary"
                      fontWeight={700}
                      sx={{ flex: 1, minWidth: 0 }}
                      noWrap
                      title={ws.workspaceLabel}
                    >
                      {ws.workspaceLabel}
                    </Typography>
                    {wsLoading ? (
                      <CircularProgress size={16} sx={{ flexShrink: 0 }} />
                    ) : onlineCount > 0 ? (
                      <Chip
                        size="small"
                        label={tApp("platformPage.workspacesOnlineUserCount", { count: onlineCount })}
                        color="success"
                        variant="outlined"
                        sx={{ fontWeight: 700, flexShrink: 0, height: 22, fontSize: "0.7rem" }}
                      />
                    ) : activeCount > 0 ? (
                      <Chip
                        size="small"
                        label={`${activeCount} ${tLive("dashboardPage.live.away", { defaultValue: "ausentes" })}`}
                        color="warning"
                        variant="outlined"
                        sx={{ fontWeight: 700, flexShrink: 0, height: 22, fontSize: "0.7rem" }}
                      />
                    ) : wsLoaded ? null : (
                      <Chip
                        size="small"
                        label={tApp("platformPage.portalTeamTapToLoad", { defaultValue: "Abrir" })}
                        variant="outlined"
                        sx={{ flexShrink: 0, height: 22, fontSize: "0.7rem" }}
                      />
                    )}
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 0, pb: 0.5 }}>
                    {wsLoading ? (
                      <Box sx={{ py: 2, textAlign: "center" }}>
                        <CircularProgress size={22} />
                      </Box>
                    ) : null}

                    {!wsLoading && dmCount > 0 ? (
                      <SidebarSection
                        title={t("directMessages")}
                        count={dmCount}
                        unreadCount={dmUnread}
                        collapsed={isSectionCollapsed(slug, "dms")}
                        onToggleCollapse={() => toggleSection(slug, "dms")}
                      >
                        <List dense disablePadding>
                          {(ws.conversations || []).map((item) => {
                            const selected =
                              slug === activeWorkspace && Number(item.id) === Number(activeSpaceId);
                            return (
                              <PortalTeamInboxItem
                                key={`${slug}-${item.id}`}
                                item={item}
                                selected={selected}
                                hideWorkspaceLabel
                                onSelect={() => onSelectConversation(slug, item.id)}
                              />
                            );
                          })}
                        </List>
                      </SidebarSection>
                    ) : null}

                    {!wsLoading && activeCount > 0 ? (
                      <SidebarSection
                        title={t("teamPresenceTitle")}
                        count={activeCount}
                        collapsed={isSectionCollapsed(slug, "roster")}
                        onToggleCollapse={() => toggleSection(slug, "roster")}
                      >
                        <List dense disablePadding>
                          {(ws.activeUsers || []).map((user) => {
                            const existing = findUserDmSpace(ws.conversations, user.userId);
                            const selected =
                              existing &&
                              slug === activeWorkspace &&
                              Number(existing.id) === Number(activeSpaceId);
                            const userKey = `${slug}:${user.userId}`;
                            const isOpening = openingUserKey === userKey;

                            return (
                              <PortalTeamUserRow
                                key={userKey}
                                user={user}
                                selected={Boolean(selected)}
                                loading={isOpening}
                                disabled={Boolean(openingUserKey && !isOpening)}
                                onSelect={() => onOpenUser(ws, user, existing)}
                              />
                            );
                          })}
                        </List>
                      </SidebarSection>
                    ) : null}

                    {!wsLoading && !dmCount && !activeCount ? (
                      <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                        {t("noDms")}
                      </Typography>
                    ) : null}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export { flatConversationsFromDirectory };
export default PortalTeamSidebar;

function flatConversationsFromDirectory(directory) {
  const workspaces = Array.isArray(directory) ? directory : [];
  const out = [];
  for (const ws of workspaces) {
    for (const c of ws.conversations || []) {
      out.push({
        ...c,
        workspaceSlug: ws.workspaceSlug,
        workspaceLabel: ws.workspaceLabel,
      });
    }
  }
  return out;
}
